import {Center} from 'src/design/atoms/Center';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import React, {useState} from 'react';
import type {
  InspectionTypeRequest,
  InspectionTypeResponse,
} from 'src/api/ProactApiClient';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {BackBar} from 'src/app/layout/BackBar';
import {toFormDataV2} from 'src/app/forms/engine/formData';
import {ProactForm, ProactFormGrid} from 'src/app/forms/engine/ProactForm';
import {useEntitySyncedForm} from 'src/app/forms/engine/useEntitySyncedForm';
import {SidebarLinkInspections} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {
  useCreateInspectionTypeMutation,
  useGetInspectionTypeQuery,
  useUpdateInspectionTypeMutation,
} from 'src/redux/queries/inspectionType';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {
  InspectionTypeDocumentTitle,
  InspectionTypeFormMeta,
  MapInspectionTypeResponseToInspectionTypeRequest,
} from './inspectionTypeHelpers';
import {useBackNavigation} from 'src/shared/hooks/useBackNavigation';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export interface InspectionTypeFormProps {
  id?: number;
  mode: 'create' | 'edit';
}

export const InspectionTypeForm: React.FC<InspectionTypeFormProps> = ({
  id,
  mode,
}) => {
  const {setAlert} = useAlert();
  const navigate = useAppNavigate();
  const [isSavePending, setIsSavePending] = useState(false);

  const isCreateMode = mode === 'create';
  const isEditMode = mode === 'edit';

  const {data: fetchedItem, isLoading: isItemLoading} =
    useGetInspectionTypeQuery(id!, {
      skip: !isEditMode || id === undefined,
    });

  const [createInspectionType] = useCreateInspectionTypeMutation();
  const [updateInspectionType] = useUpdateInspectionTypeMutation();

  const item: InspectionTypeResponse | undefined = fetchedItem;
  const isLoading = isEditMode && isItemLoading;

  const form = useEntitySyncedForm({
    formMeta: InspectionTypeFormMeta,
    entity: fetchedItem,
    toFormData: toFormDataV2,
  });

  const handleSave = async () => {
    setIsSavePending(true);

    try {
      const values = form.getFieldValues();
      const model: InspectionTypeRequest =
        MapInspectionTypeResponseToInspectionTypeRequest({
          ...values,
          id: item?.id,
        } as InspectionTypeResponse);

      let response: InspectionTypeResponse;

      if (isCreateMode) {
        response = await createInspectionType(model).unwrap();
        setAlert(`${response.name} was successfully created`, 'success');
      } else {
        response = await updateInspectionType(model).unwrap();
        setAlert(
          `${item?.name ?? 'Inspection Type'} was successfully updated`,
          'success'
        );
      }

      navigate(PathUtility.InspectionTypesView(response.id), {replace: true});
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }

    setIsSavePending(false);
  };

  const handleValidateAndSave = async () => {
    const isValid = await form.validateForm();
    if (isValid) {
      void handleSave();
    }
  };

  const {goBack: handleCancel} = useBackNavigation(
    isEditMode && id
      ? PathUtility.InspectionTypesView(id)
      : PathUtility.Inspections('inspection-types')
  );

  if (isLoading) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  const pageTitle = isCreateMode
    ? 'New Inspection Type'
    : item?.name ?? 'Edit Inspection Type';

  return (
    <Stack>
      <BackBar
        fallbackTo={
          isEditMode && id
            ? PathUtility.InspectionTypesView(id)
            : PathUtility.Inspections('inspection-types')
        }
        fallbackLabel={
          isEditMode ? 'Back to Inspection Type' : 'Back to Inspection Types'
        }
        breadcrumbs={[
          {
            label: InspectionTypeDocumentTitle,
            to: PathUtility.Inspections('inspection-types'),
          },
          ...(isEditMode && item
            ? [
                {
                  label: item.name ?? 'Inspection Type',
                  to: PathUtility.InspectionTypesView(id!),
                },
              ]
            : []),
          {label: isCreateMode ? 'New Inspection Type' : 'Edit'},
        ]}
      />

      <PageHeader>
        <PageHeader.Title icon={SidebarLinkInspections.icon}>
          {pageTitle}
        </PageHeader.Title>

        {isEditMode && (
          <PageHeader.Badges>
            <PageHeader.Badge color="yellow" variant="light">
              Editing
            </PageHeader.Badge>
          </PageHeader.Badges>
        )}

        <PageHeader.Actions>
          <PageHeader.Action variant="cancel" onClick={handleCancel}>
            Cancel
          </PageHeader.Action>
          <PageHeader.Action
            variant={isCreateMode ? 'create' : 'save'}
            onClick={handleValidateAndSave}
            loading={isSavePending}>
            {isCreateMode ? 'Create Inspection Type' : 'Save Changes'}
          </PageHeader.Action>
        </PageHeader.Actions>
      </PageHeader>

      <ProactForm form={form}>
        <ProactFormGrid />
      </ProactForm>

      <Group>
        <PageHeader.Action variant="cancel" onClick={handleCancel}>
          Cancel
        </PageHeader.Action>
        <PageHeader.Action
          variant={isCreateMode ? 'create' : 'save'}
          onClick={handleValidateAndSave}
          loading={isSavePending}>
          {isCreateMode ? 'Create Inspection Type' : 'Save Changes'}
        </PageHeader.Action>
      </Group>
    </Stack>
  );
};
